import classNames from 'classnames';
import * as React from 'react';
import { RefObject } from 'react';

import { Bem } from '../../service/Bem/Bem';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';

import './ExpandedEditBlock.css';

export interface ExpandedEditBlockProps {
    header?: React.ReactNode;
    footer?: React.ReactNode;
    cls?: string;
    isOpen: boolean;
    onCloseClick?: () => void;
    onOpenClick?: () => void;
    showOpenControl: boolean;
    showCloseControl: boolean;
    isOpenDisabled?: boolean;
    disabled?: boolean;
    iconType?: Shape2Icon.edit | Shape2Icon.verticalArrow | Shape2Icon.close;
}

const editCardControlBlock = 'expanded-edit-block';

export class ExpandedEditBlock extends React.Component<ExpandedEditBlockProps> {
    private headerRef: RefObject<HTMLDivElement> = React.createRef();

    render() {
        const { isOpen, children, cls, disabled } = this.props;

        return (
            <div
                className={classNames(
                    cls,
                    Bem.cls({
                        block: editCardControlBlock,
                        mods: {
                            opened: isOpen ? 'yes' : 'no',
                            disabled: Boolean(disabled)
                        }
                    })
                )}
            >
                {this.renderHeader()}
                <div className={Bem.cls(editCardControlBlock, 'content')}>
                    {children}
                </div>
                {this.renderFooter()}
            </div>
        );
    }

    private renderHeader = () => {
        const { header, isOpen, isOpenDisabled } = this.props;

        if (!header) {
            return undefined;
        }

        const mods = { 'cursor-disabled': isOpenDisabled };

        return (
            <div
                className={Bem.cls(editCardControlBlock, 'header', mods)}
                onClick={this.toggleView}
                ref={this.headerRef}
            >
                <div className={Bem.cls(editCardControlBlock, 'title', mods)}>
                    {header}
                </div>
                {isOpen ? this.getCloseControl() : this.getOpenControl()}
            </div>
        );
    };

    private toggleView = (event: React.MouseEvent<HTMLDivElement>) => {
        const { isOpen, iconType, disabled } = this.props;

        if (disabled) {
            return;
        }

        let target = event.target as HTMLElement;

        while (target !== this.headerRef.current) {
            if (target.tagName === 'A') {
                return;
            }

            if (target.parentElement !== null) {
                target = target.parentElement;
            }
        }

        if (isOpen && iconType === Shape2Icon.verticalArrow) {
            this.onCloseClick();
        } else {
            this.onOpenClick();
        }
    };

    private renderFooter = () => {
        const { footer } = this.props;

        if (!footer) {
            return undefined;
        }

        return (
            <div className={Bem.cls(editCardControlBlock, 'footer')}>
                {footer}
            </div>
        );
    };

    private getOpenControl = () => {
        const {
            showOpenControl,
            disabled,
            isOpen,
            iconType = Shape2Icon.edit,
            isOpenDisabled
        } = this.props;

        if (!showOpenControl || disabled) {
            return null;
        }

        return (
            <Shape2
                cls={Bem.cls({
                    block: editCardControlBlock,
                    elem: 'control-element',
                    mods: {
                        iconType: iconType,
                        opened: isOpen ? 'yes' : 'no'
                    }
                })}
                hoverable={true}
                theme={Shape2Theme.light}
                icon={iconType}
                disabled={isOpenDisabled}
                width={14}
                height={14}
                onClick={this.onOpenClick}
            />
        );
    };

    private getCloseControl = () => {
        const {
            showCloseControl,
            isOpen,
            iconType = Shape2Icon.close,
            isOpenDisabled
        } = this.props;

        if (!showCloseControl) {
            return null;
        }

        return (
            <Shape2
                cls={Bem.cls({
                    block: editCardControlBlock,
                    elem: 'control-element',
                    mods: {
                        iconType: iconType,
                        opened: isOpen ? 'yes' : 'no'
                    }
                })}
                hoverable={true}
                theme={Shape2Theme.light}
                icon={iconType}
                disabled={isOpenDisabled}
                width={14}
                height={14}
                onClick={this.onCloseClick}
            />
        );
    };

    private onOpenClick = () => {
        const { isOpenDisabled, onOpenClick } = this.props;

        if (!isOpenDisabled && onOpenClick) {
            onOpenClick();
        }
    };

    private onCloseClick = () => {
        const { onCloseClick } = this.props;

        if (onCloseClick) {
            onCloseClick();
        }
    };
}
